import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Alert from '@mui/material/Alert'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { listAgents, submitAgent, approveAgent, rejectAgent, updateAgent, deleteAgent, getRd, type Agent, type AgentTier, type AgentStatus, type ResearchDomain } from '../services/authApi'

const TIER_COLOR: Record<AgentTier, 'success' | 'info' | 'warning'> = { trusted: 'success', standard: 'info', restricted: 'warning' }
const TIER_HELP: Record<AgentTier, string> = {
  trusted: 'Rival-built, audited — broadest agent access',
  standard: 'Ops-built, reviewed — narrower access',
  restricted: 'User/vibe-coded — least trusted, read-only by default',
}
const STATUS_COLOR: Record<AgentStatus, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning', active: 'success', rejected: 'error', suspended: 'default',
}

export function AgentsPage() {
  const { rdId } = useParams<{ rdId: string }>()
  const navigate = useNavigate()
  const [rd, setRd] = useState<ResearchDomain | null>(null)
  const [agents, setAgents] = useState<Agent[]>([])
  const [error, setError] = useState('')
  const [submitOpen, setSubmitOpen] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  // approve / reject dialogs
  const [approveTarget, setApproveTarget] = useState<Agent | null>(null)
  const [approveTier, setApproveTier] = useState<AgentTier>('standard')
  const [rejectTarget, setRejectTarget] = useState<Agent | null>(null)
  const [rejectReason, setRejectReason] = useState('')

  const load = useCallback(async () => {
    if (!rdId) return
    try {
      const [rdData, agentsData] = await Promise.all([getRd(rdId), listAgents(rdId)])
      setRd(rdData.rd)
      setAgents(agentsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents')
    }
  }, [rdId])

  useEffect(() => { load() }, [load])

  function guard<T>(p: Promise<T>) {
    return p.then(load).catch(err => setError(err instanceof Error ? err.message : 'Action failed'))
  }

  async function handleSubmit() {
    if (!rdId || !name) return
    await guard(submitAgent(rdId, { name, description: description || undefined }))
    setSubmitOpen(false); setName(''); setDescription('')
  }
  async function doApprove() {
    if (!rdId || !approveTarget) return
    await guard(approveAgent(rdId, approveTarget.id, approveTier))
    setApproveTarget(null); setApproveTier('standard')
  }
  async function doReject() {
    if (!rdId || !rejectTarget || !rejectReason.trim()) return
    await guard(rejectAgent(rdId, rejectTarget.id, rejectReason.trim()))
    setRejectTarget(null); setRejectReason('')
  }

  if (!rdId) return null

  const pending = agents.filter(a => a.status === 'pending')
  const reviewed = agents.filter(a => a.status !== 'pending')

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate(`/rds/${rdId}`)} size="small"><ArrowBackIcon fontSize="small" /></IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3">Agent Registry</Typography>
          <Typography variant="body2" color="text.secondary">{rd?.name} — AI agents acting on behalf of users (OBO)</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSubmitOpen(true)}>Submit Agent</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 2 }}>
        Anyone (Ops, RD admin, or a Ros user) can <strong>submit</strong> an agent — it lands <strong>pending</strong> and <strong>cannot act until a platform admin approves it</strong>. The approver assigns the trust tier; only <strong>active</strong> agents authorize at check time. Edit each tier's permissions in the <strong>Policy Matrix</strong>.
      </Alert>

      {/* Pending review queue */}
      {pending.length > 0 && (
        <Paper sx={{ border: '2px solid', borderColor: 'warning.main', mb: 3, overflow: 'hidden' }} elevation={0}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>⏳ Pending Review ({pending.length})</Typography>
          </Box>
          {pending.map(a => (
            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <SmartToyIcon color="warning" />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                {a.description && <Typography variant="caption" color="text.secondary">{a.description}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  submitted by {a.registeredBy ?? 'unknown'} · {a.id}
                </Typography>
              </Box>
              <Button size="small" variant="contained" color="success" onClick={() => setApproveTarget(a)}>Approve</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setRejectTarget(a)}>Reject</Button>
            </Box>
          ))}
        </Paper>
      )}

      {/* Reviewed agents */}
      {reviewed.length === 0 && pending.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }} elevation={0}>
          <SmartToyIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No agents yet. Submit one for review.</Typography>
        </Paper>
      ) : reviewed.length > 0 && (
        <Paper sx={{ border: '1px solid', borderColor: 'divider' }} elevation={0}>
          {reviewed.map((a, i) => (
            <Box key={a.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderTop: i ? '1px solid' : 'none', borderColor: 'divider' }}>
              <SmartToyIcon color={a.status === 'active' ? 'action' : 'disabled'} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{a.name}</Typography>
                {a.description && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{a.description}</Typography>}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace' }}>{a.id}</Typography>
                {a.status === 'rejected' && a.rejectionReason && (
                  <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>rejected: {a.rejectionReason}</Typography>
                )}
                {a.reviewedBy && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    reviewed by {a.reviewedBy}{a.reviewedAt ? ` · ${a.reviewedAt.slice(0, 10)}` : ''}
                  </Typography>
                )}
              </Box>
              <Chip label={a.status} size="small" color={STATUS_COLOR[a.status]} variant={a.status === 'active' ? 'filled' : 'outlined'} />
              {(a.status === 'active' || a.status === 'suspended') && (
                <Select
                  size="small" value={a.tier}
                  onChange={e => guard(updateAgent(rdId, a.id, { tier: e.target.value as AgentTier }))}
                  sx={{ minWidth: 140 }}
                  renderValue={(v) => <Chip label={v} size="small" color={TIER_COLOR[v as AgentTier]} />}
                >
                  {(['trusted', 'standard', 'restricted'] as AgentTier[]).map(t => (
                    <MenuItem key={t} value={t}>
                      <Box><Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{t}</Typography>
                      <Typography variant="caption" color="text.secondary">{TIER_HELP[t]}</Typography></Box>
                    </MenuItem>
                  ))}
                </Select>
              )}
              {(a.status === 'active' || a.status === 'suspended') && (
                <Button size="small" variant="outlined" onClick={() => guard(updateAgent(rdId, a.id, { status: a.status === 'active' ? 'suspended' : 'active' }))}>
                  {a.status === 'active' ? 'Suspend' : 'Reactivate'}
                </Button>
              )}
              <IconButton size="small" color="error" onClick={() => guard(deleteAgent(rdId, a.id))}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
          ))}
        </Paper>
      )}

      {/* Submit dialog */}
      <Dialog open={submitOpen} onClose={() => setSubmitOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Agent for Review</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>It will be <strong>pending</strong> until a platform admin approves it and assigns a trust tier. You can't pick the tier or self-approve.</Alert>
          <TextField fullWidth label="Name" value={name} onChange={e => setName(e.target.value)} sx={{ mb: 2 }} autoFocus />
          <TextField fullWidth label="Description / what it does" value={description} onChange={e => setDescription(e.target.value)} multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!name}>Submit for Review</Button>
        </DialogActions>
      </Dialog>

      {/* Approve dialog */}
      <Dialog open={!!approveTarget} onClose={() => setApproveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve “{approveTarget?.name}”</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Assign the trust tier this agent will operate under. It becomes <strong>active</strong> immediately.</Typography>
          <Typography variant="caption" color="text.secondary">Trust Tier</Typography>
          <Select fullWidth size="small" value={approveTier} onChange={e => setApproveTier(e.target.value as AgentTier)}>
            {(['trusted', 'standard', 'restricted'] as AgentTier[]).map(t => (
              <MenuItem key={t} value={t}><Box sx={{ textTransform: 'capitalize' }}>{t} — <Typography component="span" variant="caption" color="text.secondary">{TIER_HELP[t]}</Typography></Box></MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveTarget(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={doApprove}>Approve at {approveTier}</Button>
        </DialogActions>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject “{rejectTarget?.name}”</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Reason (recorded in the audit trail)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} multiline rows={3} sx={{ mt: 1 }} autoFocus />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doReject} disabled={!rejectReason.trim()}>Reject</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
